import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { setConsent } from "../lib/analytics";

// Cookie notice (opt-out model).
//
// Analytics and advertising cookies are enabled by default in the US-opt-out
// pattern. The banner is informational + offers an opt-out path; it doesn't
// gate the pixels. GPC / DNT signals are still respected as a hard decline
// at the analytics.js layer, so EU visitors arriving with GPC get opt-in
// behavior automatically.
//
// Can be reopened anywhere via:
//   window.dispatchEvent(new CustomEvent("sift:open-cookie-preferences"))
// The footer's "Cookie preferences" link uses this event.
export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // The banner no longer auto-shows on load (opt-out model already enables
    // analytics by default). It only opens when explicitly requested via the
    // footer / privacy-page "Cookie preferences" control, so that opt-out path
    // stays available without an intrusive banner.
    const handler = () => setOpen(true);
    window.addEventListener("sift:open-cookie-preferences", handler);
    return () => window.removeEventListener("sift:open-cookie-preferences", handler);
  }, []);

  // Not modal: the page stays usable behind it, so no Tab trap and no
  // aria-modal. But it only opens when someone asks for it, so focus moves to
  // it, Escape closes it without recording a choice, and focus goes back to
  // the link that opened it (ACCESSIBILITY_PLAN.md session 8b).
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    panelRef.current?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && typeof opener.focus === "function" && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [open]);

  if (!open) return null;

  // "Got it" persists the implicit acceptance so the banner doesn't reappear
  // every session. State is recorded explicitly so the user's choice is
  // auditable if they later challenge it.
  const handleAccept = () => {
    setConsent("accepted");
    setOpen(false);
  };
  const handleDecline = () => {
    setConsent("declined");
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-4 pointer-events-none"
    >
      <div ref={panelRef} tabIndex={-1} className="mx-auto max-w-3xl rounded-xl bg-[#111] border border-white/15 shadow-2xl p-5 text-white pointer-events-auto outline-none focus-visible:ring-2 focus-visible:ring-white/60">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="text-sm flex-1">
            <p className="font-semibold mb-1">Cookies on Sift</p>
            <p className="text-gray-300">
              We use functional cookies to keep you signed in, plus analytics
              and advertising cookies (Google Analytics, Google Tag Manager,
              and the TikTok, Meta, and Google ad measurement pixels) to
              understand how the site is used and to measure our marketing
              campaigns. You can opt out anytime, see our{" "}
              <Link to="/privacy" className="underline hover:text-white">
                privacy policy
              </Link>
              {" "}or use the Cookie preferences link in the footer.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleDecline}
              className="px-4 py-2 rounded-lg border border-white/30 text-sm hover:bg-white/10"
            >
              Opt out
            </button>
            <button
              type="button"
              onClick={handleAccept}
              className="px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-gray-200"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
